import { Component, OnDestroy, OnInit } from '@angular/core';
/**
 * IMPORTANT: RxDB creates rxjs observables outside of angulars zone
 * So you have to import the rxjs patch to ensure changedetection works correctly.
 * @link https://www.bennadel.com/blog/3448-binding-rxjs-observable-sources-outside-of-the-ngzone-in-angular-6-0-2.htm
 */
import 'zone.js/plugins/zone-patch-rxjs';
import { SignalRService } from './services/signalr.service';
import { AuthService } from './@auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { CashDeskSyncUpService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-syncup.service';
import { AuthSessionDto, ROLE_WORKSPACE_USER, TYPES_LICENCES, TYPES_LICENCES_LABELS } from './app.models';
import { CluLockscreenService, LOCKSCREEN_MODE } from './utils/clu-lockscreen/clu-lockscreen.service';
import { TranslateService } from '@ngx-translate/core';
import { CluAgentService } from './utils/clu-agent/clu-agent.service';
import { AppService } from './services/app.service';
import { BluetoothPrinterService } from './utils/bluetooth-printer-utils/bluetooth-printer.service';
import { BrowserCheckService } from './services/browser-check.service';
import { TauriService } from './services/tauri/tauri.service';
import { AuthUtils } from './utils/auth.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'HB';
  onComplete = new Subject();
  appVersion: any;
  TYPES_LICENCES = TYPES_LICENCES;
  TYPES_LICENCES_LABELS = TYPES_LICENCES_LABELS;
  testMode = false;

  allowed = true;
  browserInfo: any;

  zoom = 1;
  zoomStep = 0.1;
  minZoom = 0.5;
  maxZoom = 2;

  isTauri = this._tauriService.isTauri;

  constructor(
    private _cashDeskSyncUpService : CashDeskSyncUpService,
    private _authService: AuthService,
    private _signalrService: SignalRService,
    private _cluAgentService: CluAgentService,
    private _cluLockscreenService: CluLockscreenService,
    private _translate: TranslateService,
    private _appService: AppService,
    private _bluetoothPrinterService: BluetoothPrinterService,
    private _browserCheck: BrowserCheckService,
    private _tauriService: TauriService
  ){
    this.appVersion = _appService.appVersion$.value;
    this.testMode = (this._appService.isBackendTestEnv && window.location.href.indexOf('/cluadmin') > -1) || (this._appService.isFrontofficeTestEnv && window.location.href.indexOf('/cluadmin') == -1);

    const savedZoom = localStorage.getItem('appZoom');
    if (savedZoom) {
      this.zoom = Number(savedZoom);
      this.applyZoom();
    }

  }

  async ngOnInit() {
    this.browserInfo = this._browserCheck.getBrowserInfo();
    this.allowed = this._browserCheck.isAllowed(80); // Chrome/Edge >= 80

    this._translate.setDefaultLang(localStorage.getItem('CAURIS_LINKUP_LANG') || 'fr');

    //await this._authService.checkIfUserIsAuthenticated();
    this._cashDeskSyncUpService.start();

    this._authService.authenticatedSession$.pipe(takeUntil(this.onComplete))
    .subscribe(_authenticatedSession => {
      if (_authenticatedSession?.ROLE_ID == ROLE_WORKSPACE_USER.USER_ORDER_PORTAIL) { return; }
      this.doConnectSignalR(_authenticatedSession);
    });

    const authenticatedSession = this._authService.authenticatedSession$.value;
    if (!!authenticatedSession && authenticatedSession.ROLE_ID != ROLE_WORKSPACE_USER.USER_ORDER_PORTAIL) {
      this._signalrService.doConnectHub().then(() => {
        console.log('Hub connection established');
        this.doConnectSignalR(authenticatedSession);
      });
    }

    this._cluAgentService.initConnection();

    this._authService.isSessionLocked$.subscribe(isLocked => {
      if (isLocked) { this._cluLockscreenService.showLockscreen({ LockscreenMode: LOCKSCREEN_MODE.LOCK_SESSION }); }
      else { this._cluLockscreenService.hideLockscreen(); }
    })
  }

  private doConnectSignalR(_authenticatedSession: AuthSessionDto | null) {
    if (this._signalrService.isConnected()) {
      if (!!_authenticatedSession && !AuthUtils.isTokenExpired(_authenticatedSession.AccessToken)) {
        this._signalrService.authenticate(_authenticatedSession!.AccessToken);
      } else {
        this._signalrService.unauthenticate();
      }
    }
  }

  ngOnDestroy(): void {
    this.onComplete.next(null);
    this.onComplete.complete();

    this._cashDeskSyncUpService.stop();
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  zoomIn() {
    this.zoom = Math.min(this.maxZoom, this.zoom + this.zoomStep);
    this.applyZoom();
  }

  zoomOut() {
    this.zoom = Math.max(this.minZoom, this.zoom - this.zoomStep);
    this.applyZoom();
  }

  private applyZoom() {
    (document.body.style as any).zoom = this.zoom.toString();
    localStorage.setItem('appZoom', this.zoom.toString());
  }
}
